import { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { themeColor } from './colorThemes';
import CompareNewVsReturningModal from './CompareNewVsReturningModal';

function formatNumber(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

export default function NewVsReturningChart({ newUsers, returningUsers, themeKey, siteId }) {
  const [compareOpen, setCompareOpen] = useState(false);
  const total = newUsers + returningUsers;
  if (total === 0) {
    return (
      <p className="text-sm text-brand-outline dark:text-brand-on-surface-variant text-center py-4">
        No user data available.
      </p>
    );
  }

  const chartData = [
    { name: 'New Users', value: newUsers, color: themeColor(themeKey, 0) },
    { name: 'Returning Users', value: returningUsers, color: themeColor(themeKey, 2) },
  ];

  return (
    <div>
      {/* New vs Returning (Organic) */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <h4 className="text-sm font-semibold text-brand-on-surface dark:text-brand-outline-variant flex items-center gap-2">
          <svg className="w-4 h-4 text-brand-outline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          New vs Returning (Organic)
        </h4>
        {siteId && (
          <button
            type="button"
            onClick={() => setCompareOpen(true)}
            className="text-xs font-medium px-3 py-1.5 rounded-lg border border-brand-outline-variant dark:border-brand-outline text-brand-on-surface-variant dark:text-brand-outline hover:bg-brand-surface-container-low dark:hover:bg-brand-on-surface transition-colors flex items-center gap-1.5 font-label flex-shrink-0"
            title="Compare against a past period"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
            </svg>
            Compare
          </button>
        )}
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
        <div className="relative w-32 h-32 sm:w-40 sm:h-40 flex-shrink-0 mx-auto sm:mx-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                {chartData.map((entry, i) => (
                  <linearGradient key={i} id={`nvrGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={entry.color} stopOpacity={1} />
                    <stop offset="100%" stopColor={entry.color} stopOpacity={0.7} />
                  </linearGradient>
                ))}
              </defs>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius="40%"
                outerRadius="60%"
                paddingAngle={4}
                dataKey="value"
                stroke="none"
                cornerRadius={6}
              >
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={`url(#nvrGrad${i})`} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [formatNumber(value), name]}
                contentStyle={{
                  backgroundColor: 'rgba(255,255,255,0.98)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '12px',
                  boxShadow: '0 8px 16px -4px rgba(15, 23, 42, 0.12)',
                  padding: '8px 12px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          {/* Center total label */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-[9px] uppercase tracking-[0.18em] text-brand-outline font-label font-bold leading-none">Total</span>
            <span className="text-xl font-extrabold font-headline tabular-nums text-brand-on-surface dark:text-white leading-tight mt-0.5">
              {formatNumber(total)}
            </span>
          </div>
        </div>

        <div className="flex-1 w-full space-y-2 min-w-0">
          {chartData.map((d) => {
            const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : '0';
            return (
              <div
                key={d.name}
                className="rounded-lg border border-brand-outline-variant/60 dark:border-brand-outline/60 bg-brand-surface-container-low/50 dark:bg-brand-on-surface/30 px-3 py-2 hover:bg-brand-surface-container-low dark:hover:bg-brand-on-surface/50 transition-colors"
              >
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 ring-2 ring-white dark:ring-brand-on-surface" style={{ backgroundColor: d.color }} />
                    <span className="text-[13px] font-medium font-label text-brand-on-surface dark:text-brand-outline-variant truncate">
                      {d.name}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-1.5 flex-shrink-0">
                    <span className="text-base font-headline font-extrabold tabular-nums text-brand-on-surface dark:text-white leading-none">
                      {formatNumber(d.value)}
                    </span>
                    <span className="text-[11px] font-semibold tabular-nums text-brand-outline dark:text-brand-on-surface-variant font-label w-10 text-right">
                      {pct}%
                    </span>
                  </div>
                </div>
                <div className="mt-1.5 h-1 rounded-full bg-brand-surface-container-high dark:bg-brand-on-surface/60 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: d.color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {siteId && (
        <CompareNewVsReturningModal
          isOpen={compareOpen}
          onClose={() => setCompareOpen(false)}
          siteId={siteId}
          currentNewUsers={newUsers}
          currentReturningUsers={returningUsers}
          currentLabel="Current Period"
        />
      )}
    </div>
  );
}
